const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;
// Average lengths, used only to estimate the distance between labels
const MS_PER_MONTH = 30 * MS_PER_DAY;
const MS_PER_YEAR = 365 * MS_PER_DAY;
const MONTHS_PER_YEAR = 12;
// Larger than any (day - 1) / step, keeps day buckets of different months apart
const DAY_BUCKETS_PER_MONTH = 32;

export const TIME_UNIT_SECOND = "second";
export const TIME_UNIT_MINUTE = "minute";
export const TIME_UNIT_HOUR = "hour";
export const TIME_UNIT_DAY = "day";
export const TIME_UNIT_MONTH = "month";
export const TIME_UNIT_YEAR = "year";

// Buckets take the zone-local timestamp and the same time as a Date read with getUTC*
const fixedBucket = (stepMs) => (localMs) => Math.floor(localMs / stepMs);

const dayBucket = (step) => (localMs, date) =>
  (date.getUTCFullYear() * MONTHS_PER_YEAR + date.getUTCMonth()) * DAY_BUCKETS_PER_MONTH +
  Math.floor((date.getUTCDate() - 1) / step);

const monthBucket = (step) => (localMs, date) =>
  date.getUTCFullYear() * MONTHS_PER_YEAR + Math.floor(date.getUTCMonth() / step);

const yearBucket = (localMs, date) => date.getUTCFullYear();

const fixedLevel = (unit, unitMs, step) => ({
  unit,
  durationMs: unitMs * step,
  bucket: fixedBucket(unitMs * step),
});

/**
 * Levels of time-axis labels from the finest to the coarsest. A bar gets the
 * weight of the coarsest level whose boundary lies between it and the
 * previous bar. Each level's boundaries are also boundaries of all finer
 * levels, so "weight >= level" means "starts a new period of that level".
 */
export const TIME_LEVELS = [
  ...[1, 5, 15, 30].map((step) => fixedLevel(TIME_UNIT_SECOND, MS_PER_SECOND, step)),
  ...[1, 5, 15, 30].map((step) => fixedLevel(TIME_UNIT_MINUTE, MS_PER_MINUTE, step)),
  ...[1, 3, 6, 12].map((step) => fixedLevel(TIME_UNIT_HOUR, MS_PER_HOUR, step)),
  ...[1, 2, 4, 8, 16].map((step) => ({
    unit: TIME_UNIT_DAY,
    durationMs: MS_PER_DAY * step,
    bucket: dayBucket(step),
  })),
  ...[1, 3, 6].map((step) => ({
    unit: TIME_UNIT_MONTH,
    durationMs: MS_PER_MONTH * step,
    bucket: monthBucket(step),
  })),
  { unit: TIME_UNIT_YEAR, durationMs: MS_PER_YEAR, bucket: yearBucket },
];

const UNIT_ORDER = [
  TIME_UNIT_SECOND,
  TIME_UNIT_MINUTE,
  TIME_UNIT_HOUR,
  TIME_UNIT_DAY,
  TIME_UNIT_MONTH,
  TIME_UNIT_YEAR,
];

/**
 * Index of the first level of the next larger unit: labels at or above it
 * are bold (an hour among minutes, a day among hours, a month among days).
 *
 * @param {number} levelIndex
 * @returns {number} TIME_LEVELS.length when there is no larger unit
 */
export const majorLevelIndex = (levelIndex) => {
  const nextUnit = UNIT_ORDER[UNIT_ORDER.indexOf(TIME_LEVELS[levelIndex].unit) + 1];
  const index = TIME_LEVELS.findIndex((level) => level.unit === nextUnit);

  return index === -1 ? TIME_LEVELS.length : index;
};
